import { Signup } from '@components/auth/Signup';
import { redirectDocument, Form } from 'react-router';
import type {
  MetaFunction,
  ActionFunctionArgs,
  LoaderFunctionArgs,
} from 'react-router';
import { useActionError } from 'app/hooks/useActionError';
import { getAuthData, signUp } from 'app/utils/auth.server';
import type { IErrorMessages } from '@components/auth/types';
import { CsrfField } from '@components/CsrfField';
import { useCSRF } from 'app/hooks/useCSRF';
import { validateCSRFToken } from 'app/utils/session.server';
import { GO_URL } from 'app/utils/urls.data';
import { useLoaderDataSafe } from 'app/hooks/useLoaderDataSafe';

export const meta: MetaFunction = ({ matches }) => {
  const parentMeta = matches
    .filter((match) => match.id === 'routes/_auth')
    .flatMap((match) => match.meta || [])
    .filter((meta) => !('title' in meta));

  return [
    ...parentMeta,
    {
      title: 'Create new teacher account',
    },
  ];
};

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const data = await getAuthData(request);

  return { data };
};

export const action = async ({ request }: ActionFunctionArgs) => {
  await validateCSRFToken(request);

  const formData = await request.formData();
  const email = formData.get('email')?.toString();
  const firstName = formData.get('first-name')?.toString();
  const lastName = formData.get('last-name')?.toString();
  const password = formData.get('password')?.toString();

  if (!email || !firstName || !lastName || !password) {
    const errors = {} as IErrorMessages;
    if (!email) errors.email = 'Email is invalid';
    if (!firstName) errors.firstName = 'First name is invalid';
    if (!lastName) errors.lastName = 'Last name is invalid';
    if (!password) errors.password = 'Password is invalid';
    return { errors };
  }

  const { cookies, errors } = await signUp(
    email,
    password,
    firstName,
    lastName,
    request,
  );

  if (cookies) {
    const headers = cookies?.map((c) => ['Set-Cookie', c]) as [
      string,
      string,
    ][];

    return redirectDocument(GO_URL, {
      headers: [...headers],
    });
  }

  return { errors };
};

export default function SignupPage() {
  const { data } = useLoaderDataSafe<typeof loader>();
  const { errorMessage, releaseErrorMessage } = useActionError<
    typeof action,
    IErrorMessages
  >();
  const { csrfToken } = useCSRF();

  return (
    <Form method="POST" onChange={releaseErrorMessage}>
      <CsrfField value={csrfToken} />
      <Signup cleverUrl={data.cleverUrl} errors={errorMessage} />
    </Form>
  );
}
